import {
  Button,
  IconReset,
  SpaceBetween,
  spacing,
  styled,
  TextElement,
  TextField,
  Tooltip,
  Validation,
} from '@lessonup/ui-components';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSecondsToTime, parseTimeToSeconds } from '../../utils/time/time.utils';

export type TimeInputError = { type: 'negative'; message: string } | null;

export interface TimeInputFieldProps {
  label?: string;
  id: string;
  initialValue?: number;
  onValidChange: (newTime: number | undefined) => void;
  externalError?: TimeInputError;
  onBlur?: (time?: number) => void;
  onReset?: () => void;
  placeholder?: string;
  minTimeInSeconds?: number;
  maxTimeInSeconds?: number;
  icon?: React.ReactNode;
}

export const TimeInputField: React.FC<TimeInputFieldProps> = ({
  label,
  id,
  initialValue,
  onValidChange,
  externalError,
  onBlur,
  onReset,
  placeholder,
  maxTimeInSeconds,
  minTimeInSeconds,
  icon,
}) => {
  if (maxTimeInSeconds !== undefined && minTimeInSeconds !== undefined && maxTimeInSeconds < minTimeInSeconds) {
    throw new Error('maxTimeInSeconds must at least be equal to minTimeInSeconds');
  }

  const [inputValue, setInputValue] = useState<string>(
    initialValue !== undefined ? formatSecondsToTime(initialValue) : ''
  );

  const [internalError, setInternalError] = useState<TimeInputError>(null);
  const isUserTypingRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!isUserTypingRef.current) {
      setInputValue(initialValue !== undefined ? formatSecondsToTime(initialValue) : '');
    }
  }, [initialValue]);

  const debouncedOnValidChange = useMemo(
    () =>
      debounce((value: string) => {
        const numericValue = parseTimeToSeconds(value);
        if (numericValue !== undefined) {
          onValidChange(numericValue);
        } else {
          return;
        }
      }, 100),
    [onValidChange]
  );

  useEffect(() => {
    return () => {
      debouncedOnValidChange.cancel();
    };
  }, [debouncedOnValidChange]);

  const handleInputChange = (value: string) => {
    isUserTypingRef.current = true;
    setInputValue(value);
    debouncedOnValidChange(value);
  };

  const handleBlur = () => {
    isUserTypingRef.current = false;
    const numericValue = parseTimeToSeconds(inputValue);

    const clampedValue = clampTime(numericValue ?? 0, minTimeInSeconds, maxTimeInSeconds);

    if (!minTimeInSeconds && !maxTimeInSeconds && inputValue.trim() === '') {
      setInternalError(null);
      onValidChange(undefined);
    } else if (clampedValue === undefined) {
      setInternalError({ type: 'negative', message: t('timeInput.invalidTimeFormat') });
      onValidChange(undefined);
    } else {
      const formattedValue = formatSecondsToTime(clampedValue);
      setInputValue(formattedValue);
      setInternalError(null);
      onValidChange(clampedValue);
    }

    if (onBlur) {
      onBlur(numericValue);
    }
  };

  const handleReset = () => {
    isUserTypingRef.current = false;
    const formattedValue = initialValue !== undefined ? formatSecondsToTime(initialValue) : '';
    setInputValue(formattedValue);
    setInternalError(null);
    onValidChange(initialValue);
    if (onReset) {
      onReset();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const errorToDisplay = internalError?.type === 'negative' ? internalError : externalError;

  return (
    <SpaceBetween direction="y" spacing={spacing.size2}>
      {label && (
        <TextElement textStyle="label" weight="bold">
          {label}
        </TextElement>
      )}
      <SpaceBetween direction="x" spacing={spacing.size2}>
        <StyledTextField
          id={id}
          size="medium"
          type="text"
          value={inputValue}
          onChange={(e) => handleInputChange(e.currentTarget.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? 'HH:MM:SS.ss'}
          iconSlot={icon}
        />
        {onReset && (
          <StyledTooltip direction="top" resize="fixed" content={t('reset')}>
            <Button type="button" buttonType="neutral" iconStart={<IconReset />} onClick={handleReset} />
          </StyledTooltip>
        )}
      </SpaceBetween>
      {errorToDisplay && errorToDisplay.type === 'negative' && (
        <Validation id={`${id}-validation`} type={errorToDisplay.type} message={errorToDisplay.message} />
      )}
    </SpaceBetween>
  );
};

export function clampTime(value: number, min?: number, max?: number) {
  if (min !== undefined && value < min) {
    return min;
  }
  if (max !== undefined && value > max) {
    return max;
  }
  return value;
}

const StyledTooltip = styled(Tooltip)`
  align-self: center;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
`;
